import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { distinctUntilChanged, first, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LogCategory, LogCoreService, LoggerService, LogType } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

    // Suggested listening for AuthenticationService work: https://youtu.be/Nz0b4STz1lo 

    private apiUrl = environment.apiUrl + '/auth';

    // The current user's login state (e.g. authenticating, loggedin, refreshable, spoofing). Observe loginState$
    // to see where the current session is in the login process. THis allows us to, for example, show the overlay
    // when the user is authenticating, or limit alert polling to login states between logged in and spoofing_refreshable.
    public loginState$: BehaviorSubject<LoginState>;

    // The loggedInUser is a PersonId and it's updated anytime the logged in user changes (e.g. spoofing/unspoofing).
    // Subscribe to loggedInUser$ when you want to update the page to reflect what a different person should be seeing. 
    private _loggedInUserSubject: BehaviorSubject<number>;
    public loggedInUser$: Observable<number>;

    // token contains a deserialized representation of the current token. The tokenStateSubject indicates if that
    // token is valid, refreshable, refreshing, expired, etc. The tokenStateSubject is used interally to ensure that
    // the token is only refreshed once when it's in expired status, but it's publicly accessible so it can be viewed
    // by the environment/debugging panel. 
    public token: Token = null; 
    public tokenStateSubject: BehaviorSubject<TokenState>;

    public spoofResponse: any;

    private httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
        })
    };

    constructor(
        private http: HttpClient,
        private logger: LoggerService,
        private router: Router
    ) {
        this.log("constructor", null, LogCategory.Constructor);

        // See if there's alredy a token we can use 
        const token = localStorage.getItem('token'); 
        this.token = new Token(token || '');

        // Figure out the current login state and initialize the loginState subject with it. 
        this.loginState$ = new BehaviorSubject<LoginState>(this.loginState);

        // Initialize the loggedInUser subject with the logged in user or with zero if none.
        // Only notify observers when the value changes. 
        const userId = (this.token.isFound && (this.token.isValid || this.token.isRefreshable))
            ? this.token.payload.sub
            : 0; 
        this._loggedInUserSubject = new BehaviorSubject<number>(userId);
        this.loggedInUser$ = this._loggedInUserSubject.pipe(
            distinctUntilChanged(),
            shareReplay(1),
        );

        // Token state behaviour subject. (Yes, I did that for you, Paula.)
        this.tokenStateSubject = new BehaviorSubject<TokenState>(this.tokenState);

        // ~1-second Heartbeat to keep the login in state up to date (manages the automatic transitions like refreshable and expired)
        timer(512, 1234).subscribe(val => this.updateLoginState() );
    }

    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : ''; 
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.CoreService, LogCoreService.Authentication, category);
    }

    // Transition to loggedout status 
    private set_state_logout() {
        localStorage.removeItem('redirect_url');
        localStorage.removeItem('code');
        localStorage.removeItem('token');

        this.token = new Token('');

        this.updateLoginState(); 
    }

    // Transition to authenticating status 
    private set_state_authenticate( redirect_url : string ) {
        localStorage.setItem('redirect_url', redirect_url);
        localStorage.removeItem('code');
        localStorage.removeItem('token');
        localStorage.removeItem('unspoofToken');
        this.updateLoginState(); 
    }

    // Transition to token_exchange status 
    private set_state_exchangeToken(code: string) {
        localStorage.removeItem('redirect_url');
        localStorage.setItem('code', code);
        localStorage.removeItem('token');
        localStorage.removeItem('unspoofToken');
        this.updateLoginState();
    }

    // Transition to loggedin status 
    private set_state_login(token: string) {
        localStorage.removeItem('redirect_url');
        localStorage.removeItem('code');
        localStorage.setItem('token', token);
        // Don't add/remove/modify the unspoof token here -- that's done with spoof and unspoof 

        this.token = new Token(token);
        this.updateLoginState();
    }

    // Transition to spoofing status 
    private set_state_spoof(spoofToken: string) {
        const currentToken = localStorage.getItem('token'); 
        localStorage.removeItem('redirect_url');
        localStorage.removeItem('code');
        localStorage.setItem('token', spoofToken);
        localStorage.setItem('unspoofToken', currentToken);

        this.token = new Token(spoofToken); 
        this.updateLoginState();
        window.location.reload();
    }

    // Transition FROM spoofing status 
    private set_state_unspoof() {
        const unspoofToken = localStorage.getItem('unspoofToken'); 
        localStorage.removeItem('redirect_url');
        localStorage.removeItem('code');
        localStorage.setItem('token', unspoofToken);
        localStorage.removeItem('unspoofToken');

        this.token = new Token(unspoofToken); 
        this.updateLoginState();
    }

    // Login states:                Method used to transition
    //  'loggedout' = 0,            // logout()
    //  'authenticating',           // authenticate()
    //  'token_exchange',           // exchangeToken()
    //  'loggedin',                 // login(), unspoof()
    //  'refreshable',              // [automatic]
    //  'spoofing',                 // spoof()
    //  'spoofing_refreshable',     // [automatic]
    //  'expired',                  // [automatic]
    //  'unknown'
    private updateLoginState() {
        const currentState = this.loginState;

        // Inform subscribers only if the status has changed. 
        if (this.loginState$.value !== currentState) {

            const tokenState = this.tokenState; 

            this.log("updateLoginState", `LoginState = ${LoginState[currentState]}; TokenState = ${TokenState[tokenState]}`);
            this.loginState$.next(currentState);
            this.tokenStateSubject.next( tokenState );

            // If we have a valid or refreshable token then update the logged in user id, too. 
            const userId: number = (this.token.isFound && (this.token.isValid || this.token.isRefreshable))
                ? this.token.payload.sub
                : 0; 
            this._loggedInUserSubject.next(userId);
        }
    }

    private get tokenState() {
        // Set the static token state -- either null, valid, refreshable or expired. The "refreshing" state is set on refresh
        if (this.token.isFound) {
            if (this.token.isValid)
                return TokenState.valid;
            else if (this.token.isRefreshable)
                return TokenState.refreshable;
            else
                return TokenState.expired;
        }
        else
            return TokenState.null;
    }

    // The current login state is defined by these settings/actions:
    //      redirect_url
    //      code
    //      token
    //      unspoofToken 
    public get loginState() : LoginState {
        const hasRedirect : boolean = !!localStorage.getItem('redirect_url');
        const hasCode: boolean = !!localStorage.getItem('code');
        const hasToken: boolean = this.token.isFound;
        const hasUnspoof: boolean = !!localStorage.getItem('unspoofToken');

        if (hasToken) {
            if (this.token.isValid)
                return hasUnspoof ? LoginState.spoofing : LoginState.loggedin;
            else if (this.token.isRefreshable)
                return hasUnspoof ? LoginState.spoofing_refreshable : LoginState.refreshable;
            else
                return LoginState.expired; 
        }
        else {
            if (!hasRedirect && !hasCode)
                return LoginState.loggedout;
            else if (hasRedirect && !hasCode)
                return LoginState.authenticating;
            else if (!hasRedirect && hasCode)
                return LoginState.token_exchange;
        }

        return LoginState.unknown; 
    }

    public authenticate(pathUserWantsToVisit: string) {
        this.log("authenticate", pathUserWantsToVisit); 
        this.set_state_authenticate(pathUserWantsToVisit);
        window.location.href = environment.ssoUrl;
    }

    // Transitions user through states token_exchange and loggedin / loggedout 
    public getAccessToken( code: string ) : void {
        this.log("getAccessToken"); 
        let route: string = localStorage.getItem('redirect_url') || "dashboard";
        this.set_state_exchangeToken(code);

        this.http
            .post<String>(this.apiUrl, `code=${code}`, this.httpOptions)
            .subscribe(
                (token: string) => {
                    this.log("getAccessToken", "Token received: " + token); 
                    this.set_state_login(token);
                    this.router.navigateByUrl(route);
                },
                (error) => {
                    this.log("getAccessToken", "Token error: " + error); 
                    // console.log(error); 
                    this.set_state_logout();
                    this.router.navigateByUrl("login");
                }
            );
    }

    public spoof(pid: string): void {
        this.spoofResponse = null;
        this.http
            .post<String>(this.apiUrl + '/spoof', `Value=${pid}`, this.httpOptions)
            .subscribe(
            (token: string) => {
                this.set_state_spoof(token);
            },
            error => {
                this.spoofResponse = error;
            });
    }

    public unspoof() : void {
        this.set_state_unspoof();
        window.location.reload();
    }

    public reaudience(audiences: string): void {
        this.http
            .post<String>(this.apiUrl + '/reaudience', `Value=${audiences}`, this.httpOptions)
            .subscribe(
                (token: string) => {
                    if (token) {
                        this.set_state_login(token);
                        window.location.reload();
                    }
                    else {
                        this.logout();
                    }
                });
    }

    private refreshToken$(): Observable<string> {
        this.log("refreshToken$"); 
        return this.http
            .get<string>(this.apiUrl, this.httpOptions)
            .pipe(
                tap(
                    (token: string) => {
                        if (token) {
                            this.set_state_login(token);
                        }
                        else {
                            this.logout();
                        }
                    },
                    error => {
                        this.log("refreshToken$", error);
                        this.logout();
                    }
                )
            );
    }

    // Token token states         Possible next states
    //  0 - null (new pageload)     2
    //  1 - Valid                   3
    //  2 - Refreshable             4, 5 
    //  3 - Refreshing              2, 1
    //  4 - Expired                 1

    // This returns a finite observable of either the newly refreshed token or the next time to token state is valid 
    public tryRefreshToken$() : Observable<any> {

        // if token state is refreshable, then return an observable of the of the refreshed token 
        if (this.tokenStateSubject.value == TokenState.refreshable) {
            this.log("tryRefreshToken$", "Token is refreshable, so refresh now");
            this.tokenStateSubject.next(TokenState.refreshing);
            return this.refreshToken$();
        }
        else { // otherwise, return an observable of the next time the token state is valid so we can wait for the valid token 
            this.log("tryRefreshToken$", `Token state is ${TokenState[this.tokenStateSubject.value]}; waiting for valid token.`);
            return this.tokenStateSubject.pipe(
                first( (value) => value === TokenState.valid)
            );
        }
    }

    public logout(redirectToLogin: boolean = true): void {
        this.log("logout"); 
        this.set_state_logout();
        if ( redirectToLogin )
            window.location.href = "/login";
    }

    public get isSpoofing(): boolean {
        return ((this.loginState$.value == LoginState.spoofing)
            || (this.loginState$.value == LoginState.spoofing_refreshable))
    }

    public get isSpoofingAudience(): boolean {
        return (!this.isSpoofing && !!this.token
            && this.token.isFound
            && this.token.isValid
            && (this.token.payload.AudiencesOrig && this.token.payload.AudiencesOrig != this.token.payload.Audiences))
    }

    public get audience(): any {
        if (!!this.token
            && this.token.isFound
            && this.token.isValid)
            return this.token.payload.Audiences
        else
            return ''
    }

    public hasClaim(claim: string): boolean {
        return !!this.token
            && this.token.isValid
            && !!this.token.payload[claim]; 
    }

    public hasFeature(name: string): boolean {
        return !!this.token
            && this.token.isFound
            && this.token.isValid
            && !!this.token.payload["Features"]
            && (this.token.payload["Features"].indexOf(`|${name}|`) >= 0);
    }

    public redirectToDashboard() {
        this.log("redirectToDashboard"); 

        window.location.href = "/dashboard";
    }

    public redirectToCurrentPage(url: string) {
        this.log("redirectToCurrentPage");
        this.authenticate(url);
    }
}

// Authentication/Login states                                                            
//                                                                                  Redrct  Code    Token   Spoof
// 0) Loggedout - We have no token, redirect_url or code.                             N       N       N       -
// 1) Authenticating - We have a redirect_url (and no valid token)                    Y       N       N       -
// 2) Token Exchange - We have a code and a redirect URL (and no valid token)         N       Y       N       -
// 3) Loggedin - We have a valid token                                                -       -      Val      N
// 4) Refreshable - We have an invalid but refreshable token                          -       -      Rfh      N
// 5) Spoofing - We have a valid token and an unspoof token                           -       -      Val      Y
// 6) Spoofing_refreshable - We have an invalid but refreshable token & unspoof tok   -       -      Rfh      Y
// 7) Expired - We have an expired and non-refreshable token                          -       -      Exp      -
export enum LoginState {
    'loggedout' = 0,            // logout()
    'authenticating',           // authenticate()
    'token_exchange',           // exchangeToken()
    'loggedin',                 // login()
    'refreshable',              // [automatic]
    'spoofing',                 // spoof()
    'spoofing_refreshable',     // [automatic]
    'expired',                  // [automatic]
    'unknown'
}

export enum TokenState {
    'null' = 0,
    'valid',
    'refreshable',
    'refreshing',
    'expired'
}

export class Token {
    public isFound: boolean = false;

    public value: string; 
    public payload: any;

    private expiresOn: Date = new Date(0); 
    private refreshUntil: Date = new Date(0);

    public get validFor(): number {
        return ( this.expiresOn.getTime() - new Date().getTime()) / 1000; 
    }
    public get refreshableFor(): number {
        return ( this.refreshUntil.getTime() - new Date().getTime()) / 1000;
    }
    public get isValid(): boolean {
        return this.validFor > 0;
    }
    public get isRefreshable(): boolean {
        return this.refreshableFor > 0;
    }

    constructor(token: string) {
        this.value = token; 
        if (token) {
            if (token != "null") {
                this.isFound = true;

                this.payload = this.getPayload(token);

                this.expiresOn = new Date(0);
                this.expiresOn.setUTCSeconds(this.payload.exp);

                this.refreshUntil = new Date(0);
                this.refreshUntil.setUTCSeconds(this.payload.exp + this.payload.RefreshWindow * 60);
            }
        }
    }

    private getPayload(token): any {

        var payload = token.split(".")[1];

        // Going backwards: from bytestream, to percent-encoding, to original string.
        var decoded = decodeURIComponent(atob(payload).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(decoded);
    } 
}
